import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react'

interface UseVisibleMonthArgs {
  scrollRef: RefObject<HTMLElement | null>
  /** Map of monthKey → DOM node (month-block anchor). */
  anchorsRef: MutableRefObject<Map<string, HTMLElement | null>>
  /** Called when the top-most visible month changes. */
  onChange: (monthKey: string) => void
}

/**
 * Reports the currently "active" month using classic iOS section-header
 * semantics: the pinned title shows the month whose inline header most
 * recently crossed the top edge of the scroll container (and stays that way
 * until the next header crosses it). Scrolling back up demotes the current
 * month as soon as its header re-enters the list.
 *
 * Implementation:
 *   activeKey = the anchor whose `top` (relative to the scroll root) is
 *               the largest value ≤ 0 — i.e. the most recently scrolled-past
 *               header. If no anchor is above the top line (scrolled to the
 *               very start), we fall back to the first anchor below it.
 *
 * Anchors are the full MonthBlock `<section>` elements; their top edge
 * coincides with the inline MonthTitle for a tiny 8px padding offset, which
 * is imperceptible for this interaction.
 */
export function useVisibleMonth({ scrollRef, anchorsRef, onChange }: UseVisibleMonthArgs): void {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const root = scrollRef.current
    if (!root) return

    const recompute = () => {
      const rootRect = root.getBoundingClientRect()
      let activeKey: string | null = null
      let bestTop = Number.NEGATIVE_INFINITY // largest top ≤ 0
      let fallbackKey: string | null = null
      let fallbackTop = Number.POSITIVE_INFINITY // smallest top > 0

      anchorsRef.current.forEach((el, key) => {
        if (!el) return
        const relTop = el.getBoundingClientRect().top - rootRect.top
        if (relTop <= 0) {
          if (relTop > bestTop) {
            bestTop = relTop
            activeKey = key
          }
        } else if (relTop < fallbackTop) {
          fallbackTop = relTop
          fallbackKey = key
        }
      })

      // No local de-duplication ref here — the page may set its visible-month
      // state externally (e.g. on chip click), and a cached "last reported
      // key" inside the hook would go stale relative to that. Fire onChange
      // every frame; React deduplicates identical setState calls, and the
      // rAF gate below prevents double-work within the same frame.
      const finalKey = activeKey ?? fallbackKey
      if (finalKey) onChangeRef.current(finalKey)
    }

    // rAF-throttle so rapid scroll + MO bursts collapse into one recompute
    // per frame.
    let rafId: number | null = null
    const requestRecompute = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        recompute()
      })
    }

    const onScroll = () => requestRecompute()
    root.addEventListener('scroll', onScroll, { passive: true })

    // Pick up DOM changes when lazy-loaded MonthBlocks mount/unmount.
    // `subtree: false` is important — otherwise every framer-motion mutation
    // inside an expanding TaskEntry would trigger a recompute → setState
    // cascade per animation frame, which stutters the expand.
    const mo = new MutationObserver(requestRecompute)
    mo.observe(root, { childList: true, subtree: false })

    // Initial pass — the anchors Map is already populated by the MonthBlock
    // ref callbacks (synchronous during commit), so recompute sees the real
    // layout on first run.
    requestRecompute()

    return () => {
      root.removeEventListener('scroll', onScroll)
      mo.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [scrollRef, anchorsRef])
}
